import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { toString as luceneToString } from 'lucene';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { Batch } from '../../models/batch';
import { BatchesService } from '../../services/batches.service';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';

const batchesFields = <const>[
	'qtyBatchNet',
	'qtyBatchGross',
	'mill',
	'millDescription'
];

interface MillBatch {
	qtyBatchNet: number;
	qtyBatchGross: number;
	mill: string;
	millDescription: string;
	millRipple: boolean;
}

@Component({
	selector: 'copo-mills-stock-materials',
	templateUrl: './mills-stock-materials.component.html',
	styleUrl: './mills-stock-materials.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		NgFor,
		MatRippleModule,
		MatIconModule,

		MatButtonModule,
		LoadingSpinnerComponent,
		NumberPipe
	]
})
export class MillsStockMaterialsComponent implements OnInit, OnDestroy {
	isLoading: boolean;
	batchesAtMill?: MillBatch[];

	private batchesSub?: Subscription;

	constructor(
		private readonly batchesService: BatchesService,
		private readonly router: Router
	) {
		this.isLoading = true;
	}

	ngOnInit() {
		this.batchesSub = this.batchesService.getBatches(
			batchesFields,
			luceneToString({ field: 'statusGroup', term: '2', quoted: true }),
			0,
			5,
			new OrderBy('qtyBatchGross', OrderByDirection.DESC),
			true
		)
			.subscribe({
				next: batchesAtMillResults => {
					this.batchesAtMill = batchesAtMillResults.results.map(millBatch => ({
						qtyBatchGross: millBatch.qtyBatchGross,
						qtyBatchNet: millBatch.qtyBatchNet,
						mill: millBatch.mill,
						millDescription: millBatch.millDescription,
						millRipple: this.getQtyBatch(millBatch) !== 0
					}));

					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	ngOnDestroy() {
		this.batchesSub?.unsubscribe();
	}

	async viewDetails(millCode?: string, millDescription?: string) {
		const filter: (
			{
				id: string;
				values: {
					apiName: string;
					selected: boolean;
					value: string;
				}[][];
			} | {
				id: string;
				values: {
					code: string;
					translation: string;
				}[];
			}
		)[] = [
			{
				id: 'statusGroup',
				values: [
					[
						{
							apiName: 'statusGroup',
							selected: false,
							value: '0'
						},
						{
							apiName: 'statusGroup',
							selected: false,
							value: '1'
						},
						{
							apiName: 'statusGroup',
							selected: true,
							value: '2'
						},
						{
							apiName: 'statusGroup',
							selected: false,
							value: '3'
						},
						{
							apiName: 'statusGroup',
							selected: false,
							value: '4'
						},
						{
							apiName: 'statusGroup',
							selected: false,
							value: '5'
						}
					]
				]
			}
		];

		if (millCode && millDescription) {
			filter.push({
				id: 'mill',
				values: [{ code: millCode, translation: millDescription }]
			});
		}

		await this.router.navigateByUrl('/order-tracking/batch/datacenter', {
			state: {
				filter
			}
		});
	}

	getQtyBatch(batch: MillBatch | Batch) {
		return batch.qtyBatchGross;
	}
}
